import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { applyThemeColor } from '@/shared/lib/themeColor';

interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
  setThemeMode: (newMode: 'light' | 'dark' | 'system') => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

function resolveMode(stored: string): 'light' | 'dark' {
  if (stored === 'dark') return 'dark';
  if (stored === 'light') return 'light';
  return systemPrefersDark() ? 'dark' : 'light';
}

function getStoredTheme(): string {
  try {
    return (JSON.parse(localStorage.getItem('user_prefs') ?? '{}').theme as string) ?? 'system';
  } catch {
    return 'system';
  }
}

function saveTheme(value: string) {
  try {
    const prefs = JSON.parse(localStorage.getItem('user_prefs') ?? '{}');
    prefs.theme = value;
    localStorage.setItem('user_prefs', JSON.stringify(prefs));
  } catch {
    // storage unavailable (private mode, quota exceeded, etc.)
  }
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(() => resolveMode(getStoredTheme()));

  useEffect(() => {
    document.documentElement.classList.toggle('dark', mode === 'dark');
    applyThemeColor(mode);
  }, [mode]);

  useEffect(() => {
    const stored = getStoredTheme();
    if (stored !== 'system') return;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onMediaChange = (e: MediaQueryListEvent) =>
      setMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onMediaChange);
    return () => mq.removeEventListener('change', onMediaChange);
  }, []);

  const setThemeMode = (newMode: 'light' | 'dark' | 'system') => {
    const resolved = resolveMode(newMode);
    setMode(resolved);
    saveTheme(newMode);
  };

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      saveTheme(next);
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
